import { useEffect, useRef, useState } from "react";
import { ChevronRight, ChevronLeft } from "lucide-react";

/**
 * HorizontalScroller — wraps a horizontally scrollable strip and adds:
 *  • Gradient fade affordances on the side(s) that have more content to scroll
 *  • Tap-able chevron buttons (visible on >sm) that programmatically scroll the strip
 *
 * Use for: phase progress chips, client detail tabs, settings tabs.
 *
 * Children should be a flex row.
 */
export default function HorizontalScroller({ children, className = "", testid, chevronTestidPrefix }) {
  const ref = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const measure = () => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setShowLeft(el.scrollLeft > 4);
    setShowRight(el.scrollLeft < max - 4);
  };

  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el) return;
    el.addEventListener("scroll", measure, { passive: true });
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", measure);
      ro.disconnect();
    };
  }, []);

  const scrollBy = (dir) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.7), behavior: "smooth" });
  };

  return (
    <div className={`relative ${className}`} data-testid={testid || "horizontal-scroller"}>
      <div
        ref={ref}
        className="flex items-center gap-2 overflow-x-auto no-scrollbar scroll-smooth"
      >
        {children}
      </div>
      {showLeft && (
        <>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-paper-50 to-transparent" />
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            data-testid={`${chevronTestidPrefix || testid || "scroller"}-prev`}
            aria-label="Scroll left"
            className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white border border-paper-200 shadow-sm items-center justify-center text-ink-700 hover:bg-paper-100"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        </>
      )}
      {showRight && (
        <>
          <div className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-paper-50 to-transparent" />
          <button
            type="button"
            onClick={() => scrollBy(1)}
            data-testid={`${chevronTestidPrefix || testid || "scroller"}-next`}
            aria-label="Scroll right"
            className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white border border-paper-200 shadow-sm items-center justify-center text-ink-700 hover:bg-paper-100"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </>
      )}
    </div>
  );
}
